import React, { useState } from 'react';
import { Card, Spinner as ReactSpinner } from 'react-bootstrap';
import { SalvLabel, SalvButton, Size } from '@salvapp/ui-components';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { ApiBridgeAlert, ApiSanctionAlert } from '../api';
import './RFISending.scss';
import dayjs from 'dayjs';
import {
  BridgeApiConversationStatus,
  InternalApiAlertType,
  InternalApiManualAlert,
  InternalApiMonitoringAlert,
} from '../api-internal';
import ThreadSidePanel from './ThreadSidePanel';
import NewRequestSidePanel from './NewRequestSidePanel';
import { RFIData } from './useRfiData';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import { counterpartiesNames } from './helper';
import { RFIConfiguration } from './RFIConfiguration';
import { useMe } from '../organisation/useMe';

export interface RFISendingProps {
  alert: ApiSanctionAlert | InternalApiMonitoringAlert | ApiBridgeAlert | InternalApiManualAlert;
  alertType: InternalApiAlertType;
  rfiData: RFIData;
  onChange?: null | (() => void);
}

export const RFISending = ({ alert, alertType, rfiData, onChange = null }: RFISendingProps) => {
  const [showModal, setShowModal] = useState(false);
  const [showThread, setShowThread] = useState(false);
  const { features } = useMe();
  return (
    <>
      <Card body={false} className='rfi-sending'>
        <Card.Header>
          <div className='title-s'>Collaborative investigation</div>
          {!rfiData?.rfi && isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE_MESSAGING_OUTGOING, features) && (
            <SalvButton
              label='New request'
              size={Size.SMALL}
              variant='secondary'
              icon={faPlus}
              onClick={() => setShowModal(true)}
            />
          )}
        </Card.Header>
        {rfiData?.rfi && !rfiData.loading ? (
          <Card.Body className='rfi-body' onClick={() => setShowThread(true)}>
            {rfiData?.rfi && !rfiData.loading && (
              <div className='rfi-header'>
                <div className='information'>
                  <span className='title-s'>{counterpartiesNames(rfiData.rfi)}</span>
                  <span className='title-xs'>{rfiData.rfi.title}</span>
                  <span className='text-s text-medium'>
                    Last update:{' '}
                    {dayjs(rfiData.rfi.updatedTime ? rfiData.rfi.updatedTime : rfiData.rfi.createdTime).format(
                      'MMM DD, HH:mm'
                    )}
                  </span>
                  <span className='request-preview-labels'>
                    {rfiData?.conversation?.urgent && <SalvLabel variant='danger'>Urgent</SalvLabel>}

                    {/*
                    isConcluded === undefined => no conclusion fields for the conversation when it was closed
                    isConcluded === false => not concluded
                    */}
                    {rfiData?.conversation?.status === BridgeApiConversationStatus.CLOSED &&
                      rfiData?.conversation?.isConcluded === false && (
                        <SalvLabel variant='secondary'>Not concluded</SalvLabel>
                      )}
                  </span>
                  {isFeatureEnabled(ORGANISATION_FEATURE.RFI_SCREENING_CONFIGURATION, features) &&
                    alertType === InternalApiAlertType.SCREENING && (
                      <div className={'m-t-8'}>
                        <RFIConfiguration rfi={rfiData.rfi} reloadRfiData={rfiData.reloadRfiData} />
                      </div>
                    )}
                </div>
                <div className='buttons'>
                  <div className='text-capitalize text-strong-m'>{rfiData?.conversation?.status.toLowerCase()}</div>
                </div>
              </div>
            )}
          </Card.Body>
        ) : (
          <Card.Body>
            {!rfiData?.rfi && !rfiData.loading && (
              <div className='d-flex flex-row align-items-center'>
                <div className='flex-grow-1' />
                <span className='text-s nothing-to-show'>No request has been made about this alert.</span>
                <div className='flex-grow-1' />
              </div>
            )}
            {rfiData.loading && (
              <ReactSpinner className='loader-spinner' animation='border' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </ReactSpinner>
            )}
          </Card.Body>
        )}
      </Card>
      {!rfiData?.rfi && (
        <NewRequestSidePanel
          alert={alert}
          show={showModal}
          onComplete={(updated) => {
            setShowModal(false);
            if (updated) {
              onChange?.();
              rfiData?.reloadRfiData?.();
            }
          }}
          alertType={alertType}
        />
      )}
      {rfiData?.rfi && rfiData?.conversation && (
        <ThreadSidePanel
          alertRfi={rfiData?.rfi}
          template={rfiData?.rfiTemplate}
          show={showThread}
          onComplete={(updated) => {
            setShowThread(false);
            if (updated) {
              onChange?.();
              rfiData?.reloadRfiData?.();
            }
          }}
          conversation={rfiData.conversation}
        />
      )}
    </>
  );
};

export default RFISending;
